/**
 * @module builder
 */

import type {
  PresentationBlock,
  PresentationOptionV2,
  PresentationSectionV2,
  PresentationSectionV2Option
} from 'paintscout'
import { uuid } from '../../../util'
import { findPage, findSection, insertAt, moveWithin, removeAt, replaceAt, withPages } from './locate'
import set from 'lodash/set'
import cloneDeep from 'lodash/cloneDeep'

/**
 * CRUD over v2 **sections** — a titled group of blocks inside a page. When a presentation has
 * exactly one page, the customer-facing nav is built from section titles rather than page tabs.
 */

/**
 * Fills a section option's `INSERT_TITLE` placeholder (`advanced.titleContent` /
 * `titleContentField`) with the option's own label, so a prefab layout that ships with a
 * placeholder heading (e.g. "About Us") gets a real one instead of the literal placeholder text.
 * A no-op for options without that pair of fields set. Shared by the "Add Section" dialog and the
 * canvas drag-to-insert drop handler (#22208) so both insertion paths fill the same placeholder.
 */
export function insertSectionOptionTitle(sectionOption: PresentationSectionV2Option): PresentationSectionV2Option {
  if (!sectionOption.advanced.titleContent || !sectionOption.advanced.titleContentField) {
    return sectionOption
  }

  const newSectionOption = cloneDeep(sectionOption)
  set(
    newSectionOption,
    sectionOption.advanced.titleContentField,
    sectionOption.advanced.titleContent.replace('INSERT_TITLE', sectionOption.label)
  )
  return newSectionOption
}

export function createPresentationNextSection(args?: {
  sectionOption?: PresentationSectionV2Option
  label?: string
}): PresentationSectionV2 {
  const { sectionOption, label = 'New Section' } = args ?? {}

  const base: PresentationSectionV2 = {
    key: uuid(),
    type: 'empty',
    label,
    title: label,
    active: true,
    advanced: {
      useForNav: true,
      allowCustom: true
    },
    blocks: []
  }

  if (!sectionOption) {
    return base
  }

  // catalog entries ship with template block keys; re-key so two copies of the same prefab
  // section in one presentation don't share block keys
  return {
    ...base,
    ...sectionOption,
    key: base.key,
    blocks: (sectionOption.blocks ?? []).map((block) => ({ ...block, key: uuid() }))
  }
}

export function addPresentationNextSection(args: {
  presentation: PresentationOptionV2
  pageKey: string
  section: PresentationSectionV2
  /** Defaults to appending to the page. */
  index?: number
}): PresentationOptionV2 {
  const { presentation, pageKey, section, index } = args
  const location = findPage(presentation, pageKey)

  if (!location) {
    return presentation
  }

  const { page, pageIndex } = location
  const nextPage = {
    ...page,
    sections: insertAt(page.sections ?? [], index ?? Number.MAX_SAFE_INTEGER, section)
  }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

export function updatePresentationNextSection(args: {
  presentation: PresentationOptionV2
  section: PresentationSectionV2
}): PresentationOptionV2 {
  const { presentation, section } = args
  const location = findSection(presentation, section.key)

  if (!location) {
    return presentation
  }

  const { page, pageIndex, sectionIndex } = location
  const nextPage = { ...page, sections: replaceAt(page.sections, sectionIndex, section) }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

export function deletePresentationNextSection(args: {
  presentation: PresentationOptionV2
  sectionKey: string
}): PresentationOptionV2 {
  const { presentation, sectionKey } = args
  const location = findSection(presentation, sectionKey)

  if (!location) {
    return presentation
  }

  const { page, pageIndex, sectionIndex } = location
  const nextPage = { ...page, sections: removeAt(page.sections, sectionIndex) }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

/** Copies a section and its blocks, with fresh keys, directly below the original. */
export function copyPresentationNextSection(args: {
  presentation: PresentationOptionV2
  sectionKey: string
}): PresentationOptionV2 {
  const { presentation, sectionKey } = args
  const location = findSection(presentation, sectionKey)

  if (!location) {
    return presentation
  }

  const { page, pageIndex, section, sectionIndex } = location
  const nextPage = {
    ...page,
    sections: insertAt(page.sections, sectionIndex + 1, copySectionWithNewKeys(section))
  }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

export function copySectionWithNewKeys(section: PresentationSectionV2): PresentationSectionV2 {
  return {
    ...section,
    key: uuid(),
    blocks: (section.blocks ?? []).map((block: PresentationBlock) => ({ ...block, key: uuid() }))
  }
}

export function reorderPresentationNextSections(args: {
  presentation: PresentationOptionV2
  pageKey: string
  fromIndex: number
  toIndex: number
}): PresentationOptionV2 {
  const { presentation, pageKey, fromIndex, toIndex } = args
  const location = findPage(presentation, pageKey)

  if (!location) {
    return presentation
  }

  const { page, pageIndex } = location
  const sections = page.sections ?? []

  if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= sections.length) {
    return presentation
  }

  const nextPage = { ...page, sections: moveWithin(sections, fromIndex, toIndex) }

  return withPages(presentation, replaceAt(presentation.pages, pageIndex, nextPage))
}

/**
 * Moves a section to another page (or to a new position within its own). Handles the same-page case
 * by delegating, so callers dragging between pages don't need to special-case it.
 */
export function movePresentationNextSection(args: {
  presentation: PresentationOptionV2
  sectionKey: string
  toPageKey: string
  /** Defaults to appending to the destination page. */
  toIndex?: number
}): PresentationOptionV2 {
  const { presentation, sectionKey, toPageKey, toIndex } = args
  const from = findSection(presentation, sectionKey)
  const to = findPage(presentation, toPageKey)

  if (!from || !to) {
    return presentation
  }

  if (from.page.key === toPageKey) {
    return reorderPresentationNextSections({
      presentation,
      pageKey: toPageKey,
      fromIndex: from.sectionIndex,
      toIndex: toIndex ?? from.page.sections.length - 1
    })
  }

  const sourcePage = { ...from.page, sections: removeAt(from.page.sections, from.sectionIndex) }
  const destinationPage = {
    ...to.page,
    sections: insertAt(to.page.sections ?? [], toIndex ?? Number.MAX_SAFE_INTEGER, from.section)
  }

  let pages = replaceAt(presentation.pages, from.pageIndex, sourcePage)
  pages = replaceAt(pages, to.pageIndex, destinationPage)

  return withPages(presentation, pages)
}
